import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@base/Paper/Paper';
import { Spacings, Fonts, breakpoints } from '@styles/index';
import { getUserStatus } from '@actions/physicalNotes';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Avatar from '@base/Avatar/Avatar';
import useWindowSize from '@hooks/useWindowSize';
import { notesTypes } from '@actions-constants/index';
import { useEffect, useState } from 'react';
import { getAccessToken } from '@selectors/auth';
import physicalNotesRelatedInfoSelector from '@selectors/physicalNotes';
import { getImage } from '@utils/imageHelper';
import PhysicalNotesForm from './PhysicalNotesForm';
import AlreadyFilled from './AlreadyFilled';
import UnAuthorized from './Unauthorized';
import Success from './Success';

const Wrapper = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: 5/21;
  padding: 0px;
  background-color: initial;
  box-shadow: none;
  margin-top: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    grid-column: 1/25;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: 1/9;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const InfoContainer = styled(Paper)`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: ${Spacings.SPACING_8B};
  padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_18B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_8B};
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacings.SPACING_6B};
`;

const FormContainer = styled(Paper)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: ${Spacings.SPACING_8B};
  padding: ${Spacings.SPACING_14B} ${Spacings.SPACING_18B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_8B};
  }
`;

const StyP1 = styled(P1)`
  font-size: ${Fonts.LARGE};
`;

const StyH2 = styled(H2)`
  text-align: center;
`;

const AvatarContainer = styled.div`
  position: relative;
`;

const StyAvatar = styled(Avatar)`
  border: ${(props) =>
    props.isSpecial ? '2px solid var(--color-i-orange-1)' : 'none'};
`;

const AvatarBadge = styled.img`
  position: absolute;
  bottom: 0;
  left: 30%;
  right: 25%;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    left: 25%;
  }
`;

const showNotesComponent = (state, handleSuccessSubmit) => {
  switch (state) {
    case 'filled':
      return <AlreadyFilled />;
    case 'unauthorized':
      return <UnAuthorized />;
    case 'success':
      return <Success />;
    case 'form':
      return <PhysicalNotesForm handleSuccessSubmit={handleSuccessSubmit} />;
    default:
      return null;
  }
};

const PhysicalNotes = () => {
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const accessToken = useSelector(getAccessToken);
  const {
    firstName,
    lastName,
    thumbnail,
    isPlusEnrolled,
    iconicSubscriptionUIDs
  } = useSelector(physicalNotesRelatedInfoSelector);
  const [learnerType, setLearnerType] = useState('');
  const [noteState, setNoteState] = useState('');

  const handleSuccessSubmit = () => {
    setNoteState('success');
  };

  useEffect(() => {
    if (accessToken) {
      dispatch(getUserStatus()).then((res) => {
        if (res?.statusCode === 403) setNoteState('unauthorized');
        else if (res?.type === `${notesTypes.GET_USER_STATUS}_SUCCESS`) {
          if (res.result.has_placed_order) setNoteState('filled');
          else setNoteState('form');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    setLearnerType('');
    if (isPlusEnrolled) {
      setLearnerType('· Plus learner');
    }
    if (iconicSubscriptionUIDs?.length) {
      setLearnerType('· Iconic learner');
    }
  }, [iconicSubscriptionUIDs, isPlusEnrolled]);

  return (
    <Wrapper>
      <StyH2>Get all UPSC notes delivered to you</StyH2>
      <InfoContainer>
        <AvatarContainer>
          {!!thumbnail && (
            <StyAvatar
              src={thumbnail}
              size={
                isMobile ? `${Spacings.SPACING_17B}` : `${Spacings.SPACING_20B}`
              }
              isSpecial={learnerType}
              circular
              alt="avatar"
            />
          )}
          {!!learnerType && (
            <AvatarBadge src={getImage('physical-notes/iconic-badge.svg')} />
          )}
        </AvatarContainer>
        <ProfileContainer>
          <H3>
            {firstName} {lastName}
          </H3>
          <StyP1>UPSC GS {learnerType}</StyP1>
        </ProfileContainer>
      </InfoContainer>
      <FormContainer>
        {showNotesComponent(noteState, handleSuccessSubmit)}
      </FormContainer>
    </Wrapper>
  );
};

export default PhysicalNotes;
